import { navigate } from '@reach/router';
import { Button } from 'components/button/Button';
import { Dropdown } from 'components/dropdown';
import { ProgressIndicator } from 'components/progressIndicator/progressIndicator';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from 'reducers/modal';
import { FormRadioButtonsField } from '../../matchingProcess/matchingProcessPages/CustomFormFields/FormRadioButtonsField';
import useCountdown from '../../situationalJudgementTest/SJTResources/useCountdown';
import * as Yup from 'yup';
import { ErrorM } from 'helpers/ErrorM';
import {
  getReasonableAdjustmentReasons,
  requestReasonableAdjustment,
} from '../BlendedTestAPI';
import { Spinner } from 'components/spinner/Spinner';
import { TextArea } from 'components/formFields/TextArea';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

type ModalProps = {
  data: {
    title: string;
    prongID: number;
    assessmentId: number;
    projectId: number;
  };
};

type FormikValues = {
  note: string;
};

export const BlendedTestsAdjustmentModal: React.FC<ModalProps> = ({ data }) => {
  const [generalError, setGeneralError] = useState<string>();
  const [initialValues, setInitialValues] = useState<FormikValues>({
    note: '',
  });

  const dispatch = useDispatch();
  const { t } = useTranslation(
    [
      'Candidate+Journey+-+PQTest',
      'Business+Case+Adjustment+Modal',
      'Panel+Buttons',
    ],
    { i18n }
  );


  const handleSubmit = (values: FormikValues) => {
    const payload = {
      assessmentProngID: data.prongID,
      requestReasonID: 1000,
      note: values.note,
    };
    return (async () => {
      const result = await requestReasonableAdjustment(payload);
      if (result.response) {
        setGeneralError('');
        handleCloseModal();
        navigate(`/view-application/${data.assessmentId}/live/stages`, {
          state: { projectId: data.projectId },
        });
        return true;
      } else {
        setGeneralError(t('pPQInstruction_General_Error'));

        return false;
      }
    })();
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const validationSchema = Yup.object({
    note: Yup.string()
      .required(t('SSCandidate_Business_Case_Adjustment_Modal_Reason_Required', { ns: 'Business+Case+Adjustment+Modal' }))
      .typeError(t('SSCandidate_Business_Case_Adjustment_Modal_Reason_Required', { ns: 'Business+Case+Adjustment+Modal' })),
  });

  const modalStyle = {
    '--modalTitleColor': '#036C8F',
    '--footerBackgroundColor': '#f5f5f5',
    '--footerBorderColor': '#f5f5f5',
    '--primaryButtonBackgroundColor': '#731447',
    '--tertiaryButtonBackgroundColor': '#d5d5d5',
  } as React.CSSProperties;

  return (
    <div className="AbilityTestsAdjustmentModal" style={modalStyle}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, isSubmitting, values, setFieldValue }) => (
          <Form>
            <div className="AbilityTestsAdjustmentModal__content AbilityTestsModalContent Content">
              <div>
                <div className="AbilityTestsAdjustmentModal__title">
                  {t('SSCandidate_Business_Case_Adjustment_Modal_Title', { ns: 'Business+Case+Adjustment+Modal' })}
                </div>
                <div className="Paragraph">
                  {t('SSCandidate_Business_Case_Adjustment_Modal_Are_You_Sure', { ns: 'Business+Case+Adjustment+Modal' })}
                </div>
              </div>
              <div className="AbilityTestsAdjustmentModal__textinput">
                <TextArea
                  label={t('SSCandidate_Business_Case_Adjustment_Modal_Reason_Label', { ns: 'Business+Case+Adjustment+Modal' })}
                  name="note"
                  charLimit={100}
                />
              </div>
            </div>
            {generalError && (
              <div style={{padding: '0 30px'}}>
                <span className="ErrorLabel">{generalError}</span>
              </div>
            )}
            <div className="ModalFooter">
              <Button
                className="Rounded"
                priority="tertiary"
                type="button"
                onClick={handleCloseModal}
              >
                {t('SSCandidate_Panel_Buttons_Cancel', { ns: 'Panel+Buttons' })}
              </Button>
              <Button
                className="Discard Rounded"
                priority="primary"
                type="submit"
                onClick={() => {}}
              >
                {t('SSCandidate_Panel_Buttons_Submit', { ns: 'Panel+Buttons' })}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
